<template>
    <div class="pos-drawer-overlay" @click="$emit('close')">
        <aside class="pos-drawer" role="dialog" aria-modal="true" aria-labelledby="pos-drawer-title" @click.stop>
            <!-- Header -->
            <div class="drawer-header">
                <div class="drawer-heading">
                    <h6 id="pos-drawer-title" class="drawer-title">{{ title }}</h6>
                    <p class="drawer-period">Periode {{ periodLabel }}</p>
                </div>
                <button type="button" class="btn-close-drawer" aria-label="Tutup" @click="$emit('close')">
                    <i class="fas fa-times"></i>
                </button>
            </div>

            <div class="drawer-toolbar">
                <!-- Summary chips -->
                <div class="drawer-chips">
                    <template v-if="isLoading">
                        <span class="chip chip--skeleton chip--72"></span>
                        <span class="chip chip--skeleton chip--104"></span>
                        <span class="chip chip--skeleton chip--88"></span>
                    </template>
                    <template v-else>
                        <span class="chip"><strong>{{ formatCount(chips.pos) }}</strong> POS</span>
                        <span class="chip"><strong>{{ formatCount(chips.shipment) }}</strong> Pengiriman</span>
                        <span class="chip"><strong>{{ formatCount(chips.qty) }}</strong> QR</span>
                    </template>
                </div>

                <!-- Search -->
                <div class="drawer-search">
                    <i class="fas fa-search search-icon"></i>
                    <input
                        v-model="keyword"
                        type="text"
                        class="search-input"
                        placeholder="Cari dealer atau POS..."
                        aria-label="Cari dealer atau POS"
                        :disabled="isEmpty"
                    />
                </div>
            </div>

            <!-- Table -->
            <div class="drawer-body">
                <table v-if="!isEmpty && !isNotFound" class="drawer-table">
                    <thead>
                        <tr v-if="isLoading">
                            <th><span class="cell-skeleton cell-skeleton--80"></span></th>
                            <th></th>
                            <th><span class="cell-skeleton cell-skeleton--96 ml-auto"></span></th>
                        </tr>
                        <tr v-else>
                            <th>Dealer / POS</th>
                            <th class="text-right">Pengiriman</th>
                            <th class="text-right">Total QR</th>
                        </tr>
                    </thead>
                    <tbody v-if="isLoading">
                        <tr v-for="n in 7" :key="n">
                            <td><span class="cell-skeleton cell-skeleton--160"></span></td>
                            <td></td>
                            <td><span class="cell-skeleton cell-skeleton--96 ml-auto"></span></td>
                        </tr>
                    </tbody>
                    <tbody v-else>
                        <template v-for="dealer in summary">
                            <tr :key="`dealer-${dealer.dealerCode}`" class="row-dealer">
                                <td>{{ dealer.dealerCode }} – {{ dealer.dealerName }}</td>
                                <td class="text-right">{{ formatCount(dealer.shipment) }}</td>
                                <td class="text-right">{{ formatCount(dealer.qty) }}</td>
                            </tr>
                            <tr v-for="pos in dealer.children" :key="`pos-${dealer.dealerCode}-${pos.posCode}`" class="row-pos">
                                <td>{{ pos.posCode }} – {{ pos.posName }}</td>
                                <td class="text-right">{{ formatCount(pos.shipment) }}</td>
                                <td class="text-right">{{ formatCount(pos.qty) }}</td>
                            </tr>
                        </template>
                    </tbody>
                </table>

                <div v-if="isEmpty" class="drawer-state">
                    <span class="state-icon"><i class="fas fa-cube"></i></span>
                    <h6 class="state-title">Belum ada pengiriman di periode ini</h6>
                    <p class="state-desc">Ubah rentang tanggal untuk melihat data lain.</p>
                </div>
                <div v-else-if="isNotFound" class="drawer-state">
                    <span class="state-icon"><i class="fas fa-search"></i></span>
                    <h6 class="state-title">Tidak ditemukan</h6>
                    <p class="state-desc">Tidak ada dealer atau POS yang cocok dengan kata kunci.</p>
                </div>
            </div>
        </aside>
    </div>
</template>

<script>
    import moment from 'moment'
    import debounce from 'debounce'
    import {
        fetchPosRecap,
        formatCount,
        toPosSummaryChips,
    } from '@/services/qr-issue'

    const TITLES = {
        INTRANSIT : 'Detail Intransit POS',
        RECEIVED : 'Detail Diterima POS',
    }

    export default {
        props : {
            status : { type: String, required: true, validator: (value) => value in TITLES },
            startDate : { type: String, required: true },
            endDate : { type: String, required: true }
        },
        data () {
            return {
                isLoading : true,
                summary : [],
                apiChips : null,
                keyword : '',
                appliedKeyword : '',
                requestId : 0,
            }
        },
        computed : {
            title () {
                return TITLES[this.status]
            },
            periodLabel () {
                const fmt = (date) => moment(date).format('DD-MM-YYYY')
                return `${fmt(this.startDate)} s/d ${fmt(this.endDate)}`
            },
            hasKeyword () {
                return this.appliedKeyword.trim() !== ''
            },
            chips () {
                return this.apiChips || toPosSummaryChips(this.summary)
            },
            isEmpty () {
                return !this.isLoading && !this.hasKeyword && this.summary.length === 0
            },
            isNotFound () {
                return !this.isLoading && this.hasKeyword && this.summary.length === 0
            }
        },
        watch : {
            keyword : debounce(function (value) {
                this.appliedKeyword = value
            }, 250),
            appliedKeyword () {
                this.loadSummary()
            }
        },
        mounted () {
            this.loadSummary()
        },
        methods : {
            formatCount,
            async loadSummary () {
                this.requestId += 1
                const requestId = this.requestId
                this.isLoading = true
                try {
                    const { chips, rows } = await fetchPosRecap(this.$http, {
                        status : this.status,
                        startDate : this.startDate,
                        endDate : this.endDate,
                        search : this.appliedKeyword
                    })
                    if (requestId === this.requestId) {
                        this.summary = rows
                        this.apiChips = chips
                    }
                } catch (err) {
                    // Failed request; show the empty state.
                    console.log(err)
                    if (requestId === this.requestId) {
                        this.summary = []
                        this.apiChips = null
                    }
                } finally {
                    if (requestId === this.requestId) {
                        this.isLoading = false
                    }
                }
            }
        }
    }
</script>

<style scoped>
    .pos-drawer-overlay {
        position: fixed;
        z-index: 1050;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.4);
        display: flex;
        justify-content: flex-end;
    }

    .pos-drawer {
        width: 560px;
        max-width: 100%;
        height: 100%;
        background-color: #FFFFFF;
        box-shadow: -8px 0 24px rgba(0, 0, 0, 0.12);
        display: flex;
        flex-direction: column;
    }

    .drawer-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 20px 16px 20px 24px;
        border-bottom: 1px solid #F3F3F3;
    }

    .drawer-heading {
        flex: 1;
        min-width: 0;
    }

    .drawer-title {
        margin: 0 0 4px;
        font-size: 16px;
        font-weight: 600;
        color: #2F2F2F;
    }

    .drawer-period {
        margin: 0;
        font-size: 12px;
        color: #707070;
    }

    .btn-close-drawer {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 32px;
        height: 32px;
        padding: 0;
        background: transparent;
        border: none;
        border-radius: 4px;
        font-size: 14px;
        color: #707070;
        cursor: pointer;
    }

    .btn-close-drawer:hover {
        background-color: #F8F8F8;
        color: #2F2F2F;
    }

    .drawer-toolbar {
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding: 16px 24px;
    }

    .drawer-chips {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px;
        font-size: 12px;
    }

    .chip {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 4px 12px;
        border-radius: 999px;
        background-color: #F2F2F2;
        color: #4B4B4B;
        font-size: 12px;
        line-height: 16px;
        white-space: nowrap;
    }

    .chip strong {
        font-weight: 700;
        color: #2F2F2F;
    }

    .chip--skeleton {
        height: 24px;
        padding: 0;
        background-color: #EDEDED;
    }

    .chip--72 { width: 72px; }
    .chip--104 { width: 104px; }
    .chip--88 { width: 88px; }

    .drawer-search {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        border: 1px solid #E0E0E0;
        border-radius: 4px;
    }

    .drawer-search:focus-within {
        border-color: #F36F21;
    }

    .search-icon {
        flex-shrink: 0;
        font-size: 13px;
        color: #707070;
    }

    .search-input {
        flex: 1;
        min-width: 0;
        padding: 0;
        border: none;
        outline: none;
        background: transparent;
        font-size: 12px;
        line-height: 16px;
        color: #2F2F2F;
    }

    .search-input::placeholder {
        color: #ADADAD;
    }

    .drawer-body {
        flex: 1;
        display: flex;
        flex-direction: column;
        overflow-y: auto;
        padding: 0 24px 24px 24px;
    }

    .drawer-table {
        width: 100%;
        margin: 0;
        border-collapse: collapse;
    }

    .drawer-table th,
    .drawer-table td {
        height: 40px;
        padding: 12px;
        border-top: none;
        border-bottom: 1px solid #F3F3F3;
        vertical-align: middle;
        font-size: 12px;
        color: #2F2F2F;
    }

    .drawer-table th {
        position: sticky;
        top: 0;
        z-index: 1;
        background-color: #FFFFFF;
        font-weight: 600;
        color: #707070;
    }

    .drawer-table th:nth-child(2),
    .drawer-table td:nth-child(2) {
        width: 96px;
        padding-left: 0;
        padding-right: 16px;
    }

    .drawer-table th:nth-child(3),
    .drawer-table td:nth-child(3) {
        width: 108px;
        padding-left: 0;
    }

    .row-dealer td {
        background-color: #F8F8F8;
        font-weight: 600;
        color: #2F2F2F;
    }

    .row-pos td:first-child {
        padding-left: 28px;
    }

    .cell-skeleton {
        display: block;
        height: 16px;
        border-radius: 4px;
        background-color: #EDEDED;
    }

    .cell-skeleton--80 { width: 80px; }
    .cell-skeleton--96 { width: 96px; }
    .cell-skeleton--160 { width: 160px; max-width: 100%; }

    .drawer-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        margin: auto;
        padding: 40px 0;
        text-align: center;
    }

    .state-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background-color: #F2F2F2;
        font-size: 16px;
        color: #B2B2B2;
    }

    .state-title {
        margin: 0;
        font-size: 14px;
        font-weight: 600;
        color: #2F2F2F;
    }

    .state-desc {
        margin: 0;
        font-size: 12px;
        color: #707070;
    }
</style>
